import { useCallback, useEffect, useMemo } from 'react';

import debounce from 'lodash-es/debounce';
import { useIntl } from 'react-intl';

import { AsyncSelect, OptionsType, OptionType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';

import { SEARCH_DEBOUNCE_TIME } from 'src/components/pipelines/constants';

import { loadWorkspacesAsync, mapWorkspacesToOptions } from '../utils';

import { messages } from './create-branch.i18n';
import * as styles from './create-branch.style';

export type WorkspaceSelectorProps = {
  onLoadRepositories: (owner: string, filter?: string) => void;
  onWorkspaceChange: (slug: string) => void;
  defaultOptions: OptionsType<any>;
  workspaceSlug: string;
};

export const Divider = () => (
  <hr
    style={{
      border: 0,
      borderTop: `1px solid ${token('color.border')}`,
      marginTop: token('space.250'),
      marginBottom: token('space.050'),
    }}
  />
);

export function WorkspaceSelector({
  onLoadRepositories,
  onWorkspaceChange,
  workspaceSlug,
  defaultOptions,
}: WorkspaceSelectorProps) {
  const intl = useIntl();
  const selectedValue = useMemo<OptionType>(() => {
    return { value: workspaceSlug, label: workspaceSlug } as OptionType;
  }, [workspaceSlug]);

  const debouncedLoadWorkspaces = useMemo(
    () =>
      debounce(
        async (
          searchTerm: string,
          setOptions: (options: OptionsType<any>) => void
        ) => {
          try {
            const workspaces = await loadWorkspacesAsync(searchTerm);
            const options = mapWorkspacesToOptions(workspaces);
            setOptions(options);
          } catch {
            setOptions([]);
          }
        },
        SEARCH_DEBOUNCE_TIME
      ),
    []
  );

  const loadWorkspaces = useCallback(
    (searchTerm: string = '') =>
      new Promise<OptionsType<any>>(resolve => {
        debouncedLoadWorkspaces(searchTerm, resolve);
      }),
    [debouncedLoadWorkspaces]
  );

  useEffect(() => {
    return () => {
      debouncedLoadWorkspaces.cancel();
    };
  }, [debouncedLoadWorkspaces]);

  const onChange = useCallback(
    (workspaceOption: OptionType | null) => {
      if (!workspaceOption) {
        return;
      }
      const slug = workspaceOption.value as string | undefined;
      if (slug) {
        onWorkspaceChange(slug);
        onLoadRepositories(slug);
      }
    },
    [onLoadRepositories, onWorkspaceChange]
  );

  return (
    <styles.FieldWrapper>
      <styles.FormLabel
        id="workspace-select-label"
        htmlFor="workspace-select-input"
      >
        {intl.formatMessage(messages.workspaceLabel)}
      </styles.FormLabel>
      <AsyncSelect
        inputId="workspace-select-input"
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={loadWorkspaces}
        value={selectedValue}
        onChange={onChange}
        noOptionsMessage={() =>
          intl.formatMessage(messages.workspaceSelectorNoMatches)
        }
      />
      <Divider />
    </styles.FieldWrapper>
  );
}
